// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Star20Filled, Star20Regular } from '@fluentui/react-icons';
import React, { useCallback, useState } from 'react';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    starButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        minWidth: '44px',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        transition: 'transform 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            transform: 'scale(1.15)',
        },
        '&:focus': {
            outline: `2px solid ${tokens.colorBrandStroke1}`,
            outlineOffset: '2px',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    },
    starIcon: {
        fontSize: '24px',
        color: tokens.colorNeutralForeground4,
    },
    starIconFilled: {
        color: '#FFB800', // Gold color for filled stars
    },
    starIconHover: {
        color: '#FFC700', // Slightly brighter gold for hover
    },
    label: {
        marginLeft: '12px',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        minWidth: '80px',
    },
});

interface StarRatingProps {
    /** Current rating value (1-5) */
    value: number;
    /** Callback when rating changes */
    onChange: (rating: number) => void;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Whether to show the rating label */
    showLabel?: boolean;
    /** Size variant */
    size?: 'small' | 'medium' | 'large';
}

const ratingLabels: Record<number, string> = {
    1: 'Çok Kötü',
    2: 'Kötü',
    3: 'Orta',
    4: 'İyi',
    5: 'Çok İyi',
};

/**
 * StarRating component for selecting a 1-5 rating.
 * Supports keyboard navigation (arrow keys, Enter, Tab).
 * WCAG 2.1 AA compliant with proper ARIA labels.
 */
export const StarRating: React.FC<StarRatingProps> = ({
    value,
    onChange,
    disabled = false,
    showLabel = true,
}) => {
    const classes = useClasses();
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    const [focusValue, setFocusValue] = useState<number | null>(null);

    const handleStarClick = useCallback(
        (star: number) => {
            if (!disabled) {
                onChange(star);
            }
        },
        [disabled, onChange],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent, star: number) => {
            if (disabled) return;

            switch (event.key) {
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    onChange(star);
                    break;
                case 'ArrowRight':
                case 'ArrowUp':
                    event.preventDefault();
                    if (star < 5) {
                        onChange(star + 1);
                        // Move focus to next star
                        const nextStar = document.querySelector(`[data-star="${star + 1}"]`);
                        if (nextStar instanceof HTMLButtonElement) nextStar.focus();
                    }
                    break;
                case 'ArrowLeft':
                case 'ArrowDown':
                    event.preventDefault();
                    if (star > 1) {
                        onChange(star - 1);
                        // Move focus to previous star
                        const prevStar = document.querySelector(`[data-star="${star - 1}"]`);
                        if (prevStar instanceof HTMLButtonElement) prevStar.focus();
                    }
                    break;
                default:
                    break;
            }
        },
        [disabled, onChange],
    );

    const getStarState = (star: number): 'filled' | 'hover' | 'empty' => {
        const effectiveValue = hoverValue ?? focusValue ?? value;
        if (effectiveValue >= star) {
            return hoverValue ? 'hover' : 'filled';
        }
        return 'empty';
    };

    const displayValue = hoverValue ?? value;

    return (
        <div
            className={classes.root}
            role="radiogroup"
            aria-label="Rating"
            onMouseLeave={() => { setHoverValue(null); }}
        >
            {[1, 2, 3, 4, 5].map((star) => {
                const state = getStarState(star);
                const isFilled = state === 'filled' || state === 'hover';

                return (
                    <button
                        key={star}
                        type="button"
                        data-star={star}
                        className={classes.starButton}
                        onClick={() => { handleStarClick(star); }}
                        onMouseEnter={() => { setHoverValue(star); }}
                        onFocus={() => { setFocusValue(star); }}
                        onBlur={() => { setFocusValue(null); }}
                        onKeyDown={(e) => { handleKeyDown(e, star); }}
                        disabled={disabled}
                        role="radio"
                        aria-checked={value === star}
                        aria-label={`${star} yıldız - ${ratingLabels[star]}`}
                        tabIndex={value === star || (value === 0 && star === 1) ? 0 : -1}
                    >
                        {isFilled ? (
                            <Star20Filled
                                className={mergeClasses(
                                    classes.starIcon,
                                    state === 'hover' ? classes.starIconHover : classes.starIconFilled,
                                )}
                            />
                        ) : (
                            <Star20Regular className={classes.starIcon} />
                        )}
                    </button>
                );
            })}
            {showLabel && displayValue > 0 && (
                <span className={classes.label} aria-live="polite">
                    {ratingLabels[displayValue]}
                </span>
            )}
        </div>
    );
};
